import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";

export interface ActiveFormRow {
    FORM_FRAME_ID?: string;
    DISPLAY_SORT_FRAME?: string;
    PARENT_ID?: string;
    FORM_QUESTION_ID?: string;
    QUESTION?: string;
    QUESTION_TYPE?: string;
    DATABASE_FIELD_NAME?: string;
    DISPLAY_SORT_QUESTION?: string;
    ANSWER_TYPE?: string;
    ANSWER_RENDERER?: string;
    CUSTOMER_VISIBLE_QUESTION?: string;
    ANSWER?: string;
    VALUE?: string;
    FORM_ANSWER_ID?: string | null;
    DISPLAY_SORT_ANSWER?: string;
    FORM_ID?: string;
    NAME?: string;
    VERSION?: string;
    FRAMES?: unknown;
    SELECTED?: boolean | string | number;
}

export type FormAnswersMap = Record<string, Record<string, string | null | undefined>>;

export interface ParsedForm {
    formId: string;
    name: string;
    version: string;
    answeredFormId?: string;
    rows: ActiveFormRow[];
}

export interface ParsedAnswerOption {
    value: string;
    answer: string;
    selected: boolean;
    renderer: string;
    sortOrder: number;
}

export interface ParsedQuestion {
    questionId: string;
    question: string;
    answerType: string;
    questionType: string;
    dbFieldName: string;
    sortOrder: number;
    options: ParsedAnswerOption[];
}

export interface ParsedFormSection {
    parentId: string;
    sortOrder: number;
    questions: ParsedQuestion[];
}

function extractRowsFromTree(nodes: ActiveFormRow[]): ActiveFormRow[] {
    const rows: ActiveFormRow[] = [];
    for (const node of nodes) {
        const qid = node.FORM_QUESTION_ID;
        if (qid != null && String(qid) !== '' && String(qid) !== '-1') {
            rows.push({ ...node, FORM_ANSWER_ID: null });
        }
        const children = (node as Record<string, unknown>).children as ActiveFormRow[] | undefined;
        if (Array.isArray(children)) {
            rows.push(...extractRowsFromTree(children));
        }
    }
    return rows;
}

function normalizeFormId(formId: string): string {
    return formId.replace(/^form_/i, "");
}

function getFormAnswer(formAnswers: FormAnswersMap, formId: string): Record<string, string | null | undefined> | undefined {
    const normalizedFormId = normalizeFormId(formId);
    return formAnswers[normalizedFormId]
        ?? formAnswers[`form_${normalizedFormId}`]
        ?? formAnswers[normalizedFormId.toUpperCase()]
        ?? formAnswers[`FORM_${normalizedFormId}`]
        ?? formAnswers[normalizedFormId.toLowerCase()]
        ?? formAnswers[`form_${normalizedFormId}`.toLowerCase()];
}

function isOptionSelected(customerValue: string, optionValue: string): boolean {
    if (customerValue.includes(',')) {
        return customerValue.split(',').map(v => v.trim()).includes(optionValue);
    }

    if (optionValue === "0") return customerValue === "0";
    return customerValue.includes(optionValue);
}

function applyAnswersToRows(rows: ActiveFormRow[], customerAnswer?: Record<string, string | null | undefined>): ActiveFormRow[] {
    if (!customerAnswer) return rows;

    return rows.map(row => {
        if (!row.DATABASE_FIELD_NAME) return row;

        const dbFieldName = String(row.DATABASE_FIELD_NAME);
        const customerValue = customerAnswer[dbFieldName]
            ?? customerAnswer[dbFieldName.toUpperCase()]
            ?? customerAnswer[dbFieldName.toLowerCase()];

        if (customerValue == null || String(customerValue) === '') return row;

        const customerStr = String(customerValue);
        const optionValue = row.VALUE == null || String(row.VALUE).trim() === '' ? null : String(row.VALUE);
        if (optionValue != null) {
            return { ...row, SELECTED: isOptionSelected(customerStr, optionValue) };
        }

        return { ...row, SELECTED: true, VALUE: customerStr };
    });
}

function hasSelectedRows(rows: ActiveFormRow[]): boolean {
    return rows.some(row => {
        const selected = row.SELECTED;
        return selected === true || selected === 1 || selected === "1" || selected === "true";
    });
}

function buildActiveForms(data: ActiveFormRow[], formAnswers: FormAnswersMap = {}, onlyAnswered = false): ParsedForm[] {
    if (data.length > 0 && data[0].FORM_ID !== undefined) {
        return data.flatMap(form => {
            const raw = form as Record<string, unknown>;
            const frames = raw.FRAMES;
            const formId = String(form.FORM_ID ?? '');
            const customerAnswer = getFormAnswer(formAnswers, formId);

            const rows: ActiveFormRow[] = [];
            if (frames) {
                const frameList: ActiveFormRow[] = Array.isArray(frames)
                    ? (frames as ActiveFormRow[])
                    : [frames as ActiveFormRow];
                rows.push(...extractRowsFromTree(frameList));
            }

            const rowsWithAnswers = applyAnswersToRows(rows, customerAnswer);
            if (onlyAnswered && !customerAnswer && !hasSelectedRows(rowsWithAnswers)) return [];

            return [{
                formId,
                name: String(raw.NAME ?? ''),
                version: String(raw.VERSION ?? ''),
                answeredFormId: String(customerAnswer?.ANSWERED_FORM_ID ?? customerAnswer?.answeredFormId ?? ""),
                rows: rowsWithAnswers,
            }];
        });
    }
    return [{ formId: '0', name: '', version: '', rows: data }];
}

function normalizeRecordMap(value: unknown): Record<string, Record<string, unknown>> {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, Record<string, unknown>>
        : {};
}

function readRawString(raw: Record<string, unknown>, key: string, fallback = ""): string {
    return String(raw[key] ?? raw[key.toLowerCase()] ?? fallback);
}

function buildFormFromDetails(rawDetails: unknown, formAnswers: FormAnswersMap): ParsedForm | null {
    const details = rawDetails && typeof rawDetails === "object" && !Array.isArray(rawDetails)
        ? rawDetails as Record<string, unknown>
        : {};
    const forms = normalizeRecordMap(details.FORM_FORM);
    const frames = normalizeRecordMap(details.FORM_FORM_FRAME);
    const questions = normalizeRecordMap(details.FORM_FORM_QUESTION);
    const answers = normalizeRecordMap(details.FORM_FORM_ANSWER);

    const formEntry = Object.values(forms)[0];
    if (!formEntry) return null;

    const formId = readRawString(formEntry, "FORM_ID");
    const rows: ActiveFormRow[] = [];

    Object.values(questions).forEach(question => {
        const questionId = readRawString(question, "FORM_QUESTION_ID");
        const frameId = readRawString(question, "FORM_FRAME_ID", "0");
        const frame = frames[frameId] ?? {};
        const answerRows = Object.values(answers).filter(answer =>
            readRawString(answer, "FORM_QUESTION_ID") === questionId
        );
        const baseRow: ActiveFormRow = {
            FORM_FRAME_ID: readRawString(frame, "FORM_FRAME_ID", frameId),
            DISPLAY_SORT_FRAME: readRawString(frame, "DISPLAY_SORT"),
            PARENT_ID: frameId,
            FORM_QUESTION_ID: questionId,
            QUESTION: readRawString(question, "TEXT"),
            QUESTION_TYPE: readRawString(question, "QUESTION_TYPE"),
            DATABASE_FIELD_NAME: readRawString(question, "DATABASE_FIELD_NAME"),
            DISPLAY_SORT_QUESTION: readRawString(question, "DISPLAY_SORT"),
            ANSWER_TYPE: readRawString(question, "ANSWER_TYPE", "text"),
            ANSWER_RENDERER: readRawString(question, "ANSWER_RENDERER"),
            CUSTOMER_VISIBLE_QUESTION: readRawString(question, "CUSTOMER_VISIBLE", "1"),
        };

        if (answerRows.length === 0) {
            rows.push(baseRow);
            return;
        }

        answerRows.forEach(answer => {
            rows.push({
                ...baseRow,
                ANSWER: readRawString(answer, "TEXT"),
                VALUE: readRawString(answer, "VALUE"),
                FORM_ANSWER_ID: readRawString(answer, "FORM_ANSWER_ID"),
                DISPLAY_SORT_ANSWER: readRawString(answer, "DISPLAY_SORT"),
            });
        });
    });

    return {
        formId,
        name: readRawString(formEntry, "NAME"),
        version: readRawString(formEntry, "VERSION"),
        answeredFormId: readRawString(getFormAnswer(formAnswers, formId) ?? {}, "ANSWERED_FORM_ID"),
        rows: applyAnswersToRows(rows, getFormAnswer(formAnswers, formId)),
    };
}

export function parseActiveFormRows(rows: ActiveFormRow[]): ParsedFormSection[] {
    const sectionMap  = new Map<string, Map<string, ParsedQuestion>>();
    const sectionSort = new Map<string, number>();

    for (const row of rows) {
        if (!row.FORM_QUESTION_ID) continue;
        if (row.CUSTOMER_VISIBLE_QUESTION === "0") continue;

        const pid = row.PARENT_ID ?? "0";
        const qid = row.FORM_QUESTION_ID;

        if (!sectionMap.has(pid)) {
            sectionMap.set(pid, new Map());
            sectionSort.set(pid, parseInt(row.DISPLAY_SORT_FRAME ?? "0", 10));
        }
        const qMap = sectionMap.get(pid)!;

        if (!qMap.has(qid)) {
            qMap.set(qid, {
                questionId: qid,
                question: row.QUESTION ?? "",
                answerType: (row.ANSWER_TYPE ?? "text").toLowerCase(),
                questionType: (row.QUESTION_TYPE ?? "full_size").toLowerCase(),
                dbFieldName: row.DATABASE_FIELD_NAME ?? "",
                sortOrder: parseInt(row.DISPLAY_SORT_QUESTION ?? "0", 10),
                options: [],
            });
        }

        const q = qMap.get(qid)!;
        const optValue  = row.VALUE  ?? "";
        const optAnswer = row.ANSWER ?? "";
        // Déduplique les options identiques (même VALUE + même ANSWER) pour éviter les doublons visuels.
        const alreadyExists = q.options.some(o => o.value === optValue && o.answer === optAnswer);
        if (!alreadyExists) {
            q.options.push({
                value: optValue,
                answer: optAnswer,
                selected: row.SELECTED === true || row.SELECTED === 1 || row.SELECTED === "1" || row.SELECTED === "true",
                renderer: (row.ANSWER_RENDERER ?? "half_size").toLowerCase(),
                sortOrder: parseInt(row.DISPLAY_SORT_ANSWER ?? "0", 10),
            });
        }
    }

    return Array.from(sectionMap.entries())
        .map(([parentId, qMap]) => ({
            parentId,
            sortOrder: sectionSort.get(parentId) ?? 0,
            questions: Array.from(qMap.values()).sort((a, b) => a.sortOrder - b.sortOrder),
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder);
}

const useGetActiveForms = () => {
    const { authFetch, isReady, workspaceId } = useAuthenticatedFetchWithWorkspace();
    const [activeForms, setActiveForms] = useState<ParsedForm[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const getActiveForms = useCallback(
        async (personId: string | number, initialAnswers: FormAnswersMap = {}): Promise<void> => {
            if (!isReady || !workspaceId) return;
            setIsLoading(true);
            setError("");
            try {
                const params = new URLSearchParams({
                    person_id: String(personId),
                    workspace: workspaceId,
                });
                const [formsSettled, answersSettled] = await Promise.allSettled([
                    authFetch(`/activeForms?${params.toString()}`, { method: "GET" }),
                    authFetch(`/formAnswers?${params.toString()}`, { method: "GET" }),
                ]);
                const result: unknown = formsSettled.status === 'fulfilled' ? formsSettled.value : null;
                const answersRaw: unknown = answersSettled.status === 'fulfilled' ? answersSettled.value : null;
                const formAnswers: FormAnswersMap = { ...initialAnswers };
                if (answersRaw && typeof answersRaw === 'object' && !Array.isArray(answersRaw)) {
                    Object.assign(formAnswers, answersRaw as FormAnswersMap);
                }

                let activeFormsList: ParsedForm[] = [];
                let answeredForms: ParsedForm[] = [];
                if (Array.isArray(result)) {
                    activeFormsList = buildActiveForms(result as ActiveFormRow[], formAnswers);
                    answeredForms = buildActiveForms(result as ActiveFormRow[], formAnswers, true);
                } else if (result && typeof result === "object") {
                    const r = result as Record<string, unknown>;
                    const arr = r?.list ?? r?.data ?? r?.forms ?? [];
                    if (Array.isArray(arr)) {
                        activeFormsList = buildActiveForms(arr as ActiveFormRow[], formAnswers);
                        answeredForms = buildActiveForms(arr as ActiveFormRow[], formAnswers, true);
                    }
                }

                const answeredFormIds = [...new Set(Object.keys(formAnswers).map(formId => normalizeFormId(String(formId))))];

                const loadedFormIds = new Set(answeredForms.map(form => form.formId));
                const missingFormIds = answeredFormIds.filter(formId => !loadedFormIds.has(formId));
                if (missingFormIds.length > 0) {
                    const details = await Promise.allSettled(
                        missingFormIds.map(formId => authFetch(`/form?id=${encodeURIComponent(formId)}`, { method: "GET" }))
                    );
                    const historicalForms = details.flatMap(detail => {
                        if (detail.status !== "fulfilled") return [];
                        const form = buildFormFromDetails(detail.value, formAnswers);
                        return form ? [form] : [];
                    });
                    answeredForms = [...answeredForms, ...historicalForms];
                }

                setActiveForms(answeredForms.length > 0 ? answeredForms : activeFormsList);
            } catch (err: unknown) {
                if (err instanceof Error) setError(err.message);
                else setError("Erreur lors du chargement du questionnaire");
            } finally {
                setIsLoading(false);
            }
        },
        [authFetch, isReady, workspaceId]
    );

    return { activeForms, isLoading, error, getActiveForms };
};

export default useGetActiveForms;
